// src/firebaseService.js
import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  getDocs, 
  updateDoc, 
  deleteDoc,
  query,
  where,
  increment,
  onSnapshot,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import React from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signOut,
  sendEmailVerification,
  reload,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence, 
} from 'firebase/auth';
import { sendPasswordResetEmail } from 'firebase/auth';

import { db, auth } from './firebase';

// NEW: call once on app startup (optional)
// Usage: await ensureAuthPersistence();

/**
 * Check if user has verified their card
 */
export const isCardVerified = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return false;
    
    const userData = userDoc.data();
    return userData.cardVerified === true;
  } catch (error) {
    console.error('Error checking card verification:', error);
    return false;
  }
};

export const ensureAuthPersistence = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
    console.log('✅ Auth persistence set to browserLocalPersistence');
    return { success: true };
  } catch (error) {
    console.error('Failed to set auth persistence:', error);
    return { success: false, error: error.message };
  }
};


/**
 * Validate card by charging $0.01 via your payment provider (server endpoint).
 * NOTE: This function calls a server endpoint '/api/validate-card-trial' — you must
 * implement that endpoint to interact with Stripe/Flutterwave/etc. and return JSON:
 * { success: true, chargeId: 'xxx', last4: '4242' }  OR { success: false, error: '...' }
 *
 * cardToken: tokenized card info / payment method id from client-side payment SDK
 */

  
/**
 * Atomically increment book.reviewsReceived
 */
export const incrementBookReviewsReceived = async (bookId, incrementBy = 1) => {
  try {
    await updateDoc(doc(db, 'books', bookId), {
      reviewsReceived: increment(incrementBy),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Increment book reviewsReceived error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Optionally increment requestedReviewsCompleted to keep owner/admin-aware
 */
export const incrementRequestedReviewsCompleted = async (bookId, by = 1) => {
  try {
    await updateDoc(doc(db, 'books', bookId), {
      requestedReviewsCompleted: increment(by),
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Increment requestedReviewsCompleted error:', error);
    return { success: false, error: error.message };
  }
};


// Increment reviews received count
export const incrementUserReviewsReceived = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      reviewsReceived: increment(1)
    });
    return { success: true };
  } catch (error) {
    console.error('Error incrementing reviews received:', error);
    return { success: false, error: error.message };
  }
};

export const checkDuplicateBookURL = async (amazonLink) => {
  try {
    const normalizedLink = normalizeLink(amazonLink);
    const q = query(collection(db, 'books'), where('amazonLink', '==', normalizedLink));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.size > 0) {
      const duplicateBook = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))[0]; // Get the first duplicate document

      return {
        isDuplicate: true,
        message: `This Amazon link already exists.`,
        book: duplicateBook
      };
    }

    return { isDuplicate: false };
  } catch (error) {
    console.error('Check duplicate book error:', error);
    return { isDuplicate: false, error: error.message };
  }
};

// Helper function to normalize the Amazon link
const normalizeLink = (link) => {
  if (!link) return '';
  return link.trim().toLowerCase()
    .replace(/\s+/g, '') // Remove all whitespace
    .replace(/\/+$/, '') // Remove trailing slashes
    .replace(/[\?#].*$/, '') // Remove query parameters and hash fragments
    .replace(/(https?:\/\/)?(www\.)?/, ''); // Remove 'http://', 'https://', and 'www.'
};
// ============================================
// AUTHENTICATION FUNCTIONS
// ============================================
// Add this function to firebaseService.js
export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
    return { 
      success: true, 
      message: 'Password reset email sent! Check your inbox.' 
    };
  } catch (error) {
    console.error('Password reset error:', error);
    
    let errorMessage = 'Failed to send reset email.';
    
    if (error.code === 'auth/user-not-found') {
      errorMessage = 'No account found with this email address.';
    } else if (error.code === 'auth/invalid-email') {
      errorMessage = 'Invalid email address.';
    } else if (error.code === 'auth/too-many-requests') {
      errorMessage = 'Too many requests. Please try again later.';
    }
    
    return { 
      success: false, 
      error: errorMessage 
    };
  }
};
/**
 * Sign up a new user
 */
// ============================================
// UPDATED signUpUser in firebaseService.js
// Replace your existing signUpUser function
// ============================================

export const signUpUser = async (email, password, name) => {
  try {
    // ── STEP 1: Collect device fingerprint ──────
    console.log('🔍 Collecting device fingerprint...');
    let fingerprint = null;
    let riskData = { riskScore: 0, action: 'allowed', allowed: true };

    try {
      // Import here to avoid circular deps
      const { collectDeviceFingerprint } = await import('./DeviceFingerprint');
      const { checkFingerprintBeforeSignup, countAccountsForDevice } = await import('./FraudDetection');

      fingerprint = await collectDeviceFingerprint();
      console.log('📱 Fingerprint collected:', fingerprint.fingerprintHash);

      // ── STEP 2: Check against existing accounts ──
      riskData = await checkFingerprintBeforeSignup(fingerprint);
      console.log('🎯 Risk assessment:', riskData.action, 'score:', riskData.riskScore);

      // ── STEP 3: Block if duplicate device ────────
      if (!riskData.allowed) {
        return {
          success: false,
          error: riskData.message,
          blocked: true,
          riskScore: riskData.riskScore,
        };
      }

      // ── STEP 3b: Block if this device already has 3+ accounts ──
      // Fail-open: only block on a CONFIRMED count (success:true). If the
      // count query errors, deviceCountResult.success is false and we just
      // fall through - signup proceeds, same as any other fingerprint error.
      const deviceCountResult = await countAccountsForDevice(fingerprint.deviceId);
      console.log('🔢 Device account count:', deviceCountResult);
      if (deviceCountResult.success && deviceCountResult.count >= 3) {
        return {
          success: false,
          error: "You already have an account on this device. Forgot your password? Click 'Forgotten Password' to recover it.",
          blocked: true,
          deviceLimitReached: true
        };
      }

    } catch (fpError) {
      // Never block signup due to fingerprint errors
      // Just log and continue
      console.warn('⚠️ Fingerprint check failed (non-blocking):', fpError.message);
    }

    // ── STEP 4: Create Firebase auth user ─────────
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // ── STEP 4a: Force-refresh the ID token before any Firestore write ──
    // Known Firebase gotcha: right after createUserWithEmailAndPassword,
    // the token isn't always attached to outgoing Firestore requests yet.
    // getIdToken(true) forces a refresh and waits for it, so STEP 5's
    // write is guaranteed to carry a valid token.
    await user.getIdToken(true);

    // ── STEP 4b: Send email verification link (non-blocking) ─────
    // A send failure shouldn't fail the whole signup - the user can still
    // hit "Resend verification email" on the verify-email screen.
    try {
      await sendEmailVerification(user);
      console.log('📧 Verification email sent to:', email);
    } catch (verifyError) {
      console.warn('⚠️ Could not send verification email:', verifyError.message);
    }

    // ── STEP 5: Create Firestore user document ────
    const userData = {
      id: user.uid,
      email,
      name,
      emailVerified: false,
      coins: 50,
      country: 'US',
      isAdmin: false,
      isBanned: false,
      isFlagged: riskData.riskScore >= 40,
      riskScore: riskData.riskScore || 0,
      reviewSubscription: { plan: 'free', active: false, expiresAt: null },
      promotionSubscription: { plan: 'free', active: false, expiresAt: null },
      trialUsed: false,
      reviewsSubmitted: 0,
      weeklyClaimsUsed: 0,
      weeklyClaimsResetDate: new Date().toISOString(),
      trialStartDate: new Date().toISOString(),
      isAffiliate: false,
      referralCode: null,
      affiliateJoinedAt: null,
      referredBy: null,
      referredAt: null,
      cardVerified: false,
      cardVerificationTransactionId: null,
      cardVerifiedAt: null,
      cardVerificationRefunded: false,
      cardVerificationRefundedAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    // Own try/catch so a rules/permission failure here is reported with a
    // specific, identifiable error instead of falling through to the outer
    // catch's generic message. The auth account (and verification email)
    // already exist by this point regardless of how this turns out - the
    // user stays signed in (no signOut happens in this function), which is
    // what lets this own-doc write succeed under rules keyed off
    // request.auth.uid.
    console.log('STEP 5 DEBUG: auth.currentUser =', auth.currentUser?.uid);
    console.log('STEP 5 DEBUG: user.uid =', user.uid);
    console.log('STEP 5 DEBUG: writing to users/', user.uid);
    try {
      await setDoc(doc(db, 'users', user.uid), userData);
    } catch (docError) {
      console.error('❌ STEP 5 failed - could not create user profile doc:', docError.message);
      console.error('STEP 5 FULL ERROR:', docError.code, docError.message);
      return {
        success: false,
        error: `Account created but profile setup failed: ${docError.message}`,
        step: 'create_user_doc',
      };
    }

    // ── STEP 6: Save fingerprint + alerts ─────────
    if (fingerprint) {
      try {
        const { saveFingerprintForUser, saveFraudAlert } = await import('./FraudDetection');
        await saveFingerprintForUser(user.uid, fingerprint, riskData);
        await saveFraudAlert(user.uid, fingerprint, riskData);
      } catch (saveError) {
        console.warn('⚠️ Could not save fingerprint:', saveError.message);
      }
    }

    console.log('✅ User created successfully:', name);
    return {
      success: true,
      user: userData,
      flagged: riskData.riskScore >= 40,
      needsVerification: true,
    };

  } catch (error) {
    console.error('Sign up error:', error);

    // If we already checked fingerprint and got blocked
    if (error.blocked) {
      return { success: false, error: error.message, blocked: true };
    }

    return { success: false, error: error.message };
  }
};


/**
 * Sign in existing user
 */
// Replace your existing signInUser with this version
export const signInUser = async (email, password) => {
  try {
    // Ensure persistence is set BEFORE signing in
    // This ensures sessions survive refresh / tab close / deployments
    await setPersistence(auth, browserLocalPersistence);

    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Get user data from Firestore
    const userDoc = await getDoc(doc(db, 'users', user.uid));

    if (userDoc.exists()) {
      // Update emailVerified status in Firestore if verified
      if (user.emailVerified && !userDoc.data().emailVerified) {
        await updateDoc(doc(db, 'users', user.uid), {
          emailVerified: true,
          updatedAt: serverTimestamp()
        });
      }

      // Return the Firestore user doc (not the auth object)
      return { success: true, user: { id: user.uid, ...userDoc.data() } };
    } else {
      // If for some reason the user record doesn't exist, sign them out
      await signOut(auth);
      return { success: false, error: 'User data not found' };
    }
  } catch (error) {
    console.error('Sign in error:', error);
    // Map common firebase auth error codes to friendlier messages if you want
    return { success: false, error: error.message };
  }
};


/**
 * Sign out current user
 */
export const signOutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Sign out error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// ============================================
// USER FUNCTIONS
// ============================================

/**
 * Get current user data
 */
export const getCurrentUser = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return { 
        success: true, 
        user: { 
          id: userDoc.id, 
          ...userDoc.data(),
          // Add fallbacks for required properties
          coins: userDoc.data().coins || 0,
          reviewSubscription: userDoc.data().reviewSubscription || { plan: 'free', active: false, expiresAt: null },
          promotionSubscription: userDoc.data().promotionSubscription || { plan: 'free', active: false, expiresAt: null }
        } 
      };
    }
    return { success: false, error: 'User not found' };
  } catch (error) {
    console.error('Get user error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update user data
 */
export const updateUser = async (userId, updates) => {
  try {
    await updateDoc(doc(db, 'users', userId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update user error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * ✅ NEW: Update ownerCoins for all books owned by a user
 */
export const updateUserBookOwnerCoins = async (userId, newCoins) => {
  try {
    const q = query(collection(db, 'books'), where('uploadedBy', '==', userId));
    const booksSnapshot = await getDocs(q);
    
    const updatePromises = booksSnapshot.docs.map(bookDoc => 
      updateDoc(bookDoc.ref, {
        ownerCoins: newCoins,
        updatedAt: serverTimestamp()
      })
    );
    
    await Promise.all(updatePromises);
    console.log(`✅ Updated ownerCoins to ${newCoins} for ${booksSnapshot.docs.length} books`);
    return { success: true };
  } catch (error) {
    console.error('Update user book owner coins error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all users (admin only)
 */
export const getAllUsers = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, 'users'));
    const users = usersSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, users };
  } catch (error) {
    console.error('Get all users error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// BOOK FUNCTIONS
// ============================================

/**
 * ✅ FIXED: Upload a new book with ownerCoins
 */
export const uploadBook = async (bookData, userId) => {
  try {
    // Get current user's coin balance
    const userDoc = await getDoc(doc(db, 'users', userId));
    const userCoins = userDoc.exists() ? (userDoc.data().coins || 0) : 0;
    
    const bookRef = doc(collection(db, 'books'));
    const book = {
      id: bookRef.id,
      ...bookData,
      uploadedBy: userId,
      reviewsReceived: 0,
      ownerCoins: bookData.ownerCoins || userCoins, // ✅ Store owner's current coins
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    console.log('📤 Uploading book with ownerCoins:', book.ownerCoins);

    await setDoc(bookRef, book);
    return { success: true, bookId: bookRef.id, book };
  } catch (error) {
    console.error('Upload book error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all books
 */
export const getAllBooks = async () => {
  try {
    const booksSnapshot = await getDocs(collection(db, 'books'));
    const books = booksSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, books };
  } catch (error) {
    console.error('Get all books error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's books
 */
export const getUserBooks = async (userId) => {
  try {
    const q = query(collection(db, 'books'), where('uploadedBy', '==', userId));
    const booksSnapshot = await getDocs(q);
    const books = booksSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, books };
  } catch (error) {
    console.error('Get user books error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update book
 */
export const updateBook = async (bookId, updates) => {
  try {
    await updateDoc(doc(db, 'books', bookId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update book error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 🔧 NEW: Update book file URL (after uploading to Storage)
 */
export const updateBookFileUrl = async (bookId, fileUrl, fileName) => {
  try {
    await updateDoc(doc(db, 'books', bookId), {
      bookFileUrl: fileUrl,
      bookFileName: fileName,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update book file URL error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete book
 */
export const deleteBook = async (bookId) => {
  try {
    await deleteDoc(doc(db, 'books', bookId));
    return { success: true };
  } catch (error) {
    console.error('Delete book error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listen to books in real-time
 */
export const listenToBooks = (callback) => {
  return onSnapshot(collection(db, 'books'), (snapshot) => {
    const books = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(books);
  });
};

// ============================================
// REVIEW FUNCTIONS
// ============================================

/**
 * Submit a review
 */
export const submitReview = async (reviewData) => {
  try {
    const reviewRef = doc(collection(db, 'reviews'));
    const review = {
      id: reviewRef.id,
      ...reviewData,
      status: 'pending',
      submittedAt: serverTimestamp(),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(reviewRef, review);
    return { success: true, reviewId: reviewRef.id, review };
  } catch (error) {
    console.error('Submit review error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get all reviews
 */
export const getAllReviews = async () => {
  try {
    const reviewsSnapshot = await getDocs(collection(db, 'reviews'));
    const reviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, reviews };
  } catch (error) {
    console.error('Get all reviews error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get reviews for a book
 */
export const getBookReviews = async (bookId) => {
  try {
    const q = query(collection(db, 'reviews'), where('bookId', '==', bookId));
    const reviewsSnapshot = await getDocs(q);
    const reviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, reviews };
  } catch (error) {
    console.error('Get book reviews error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get reviews by user
 */
export const getUserReviews = async (userId) => {
  try {
    const q = query(collection(db, 'reviews'), where('reviewerId', '==', userId));
    const reviewsSnapshot = await getDocs(q);
    const reviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, reviews };
  } catch (error) {
    console.error('Get user reviews error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update review status (approve/reject)
 */
export const updateReviewStatus = async (reviewId, status, qualityScore = null) => {
  try {
    const updates = {
      status,
      updatedAt: serverTimestamp()
    };

    if (status === 'approved') {
      updates.approvedAt = serverTimestamp();
      if (qualityScore) updates.qualityScore = qualityScore;
    } else if (status === 'rejected') {
      updates.rejectedAt = serverTimestamp();
    }

    await updateDoc(doc(db, 'reviews', reviewId), updates);
    return { success: true };
  } catch (error) {
    console.error('Update review status error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listen to reviews in real-time
 */
export const listenToReviews = (callback) => {
  return onSnapshot(collection(db, 'reviews'), (snapshot) => {
    const reviews = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(reviews);
  });
};

/**
 * Listen to pending reviews (admin)
 */
export const listenToPendingReviews = (callback) => {
  const q = query(collection(db, 'reviews'), where('status', '==', 'pending'));
  return onSnapshot(q, (snapshot) => {
    const reviews = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(reviews);
  });
};

// ============================================
// CLAIMED BOOKS FUNCTIONS
// ============================================

/**
 * Claim a book
 */
export const claimBook = async (bookId, userId) => {
  try {
    const claimRef = doc(collection(db, 'claimedBooks'));
    const claim = {
      id: claimRef.id,
      bookId,
      userId,
      claimedAt: serverTimestamp()
    };

    await setDoc(claimRef, claim);
    return { success: true, claimId: claimRef.id };
  } catch (error) {
    console.error('Claim book error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get user's claimed books
 */
export const getUserClaimedBooks = async (userId) => {
  try {
    const q = query(collection(db, 'claimedBooks'), where('userId', '==', userId));
    const claimsSnapshot = await getDocs(q);
    const claims = claimsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, claims };
  } catch (error) {
    console.error('Get user claimed books error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Remove claimed book (after review submission)
 */
export const removeClaimedBook = async (bookId, userId) => {
  try {
    const q = query(
      collection(db, 'claimedBooks'), 
      where('bookId', '==', bookId),
      where('userId', '==', userId)
    );
    const claimsSnapshot = await getDocs(q);
    
    const deletePromises = claimsSnapshot.docs.map(doc => deleteDoc(doc.ref));
    await Promise.all(deletePromises);
    
    return { success: true };
  } catch (error) {
    console.error('Remove claimed book error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// BOOK PROGRESS FUNCTIONS
// ============================================

/**
 * Create book progress entry
 */
export const createBookProgress = async (progressData) => {
  try {
    const progressRef = doc(collection(db, 'bookProgress'));
    const progress = {
      id: progressRef.id,
      ...progressData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    await setDoc(progressRef, progress);
    return { success: true, progressId: progressRef.id };
  } catch (error) {
    console.error('Create book progress error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update book progress
 */
export const updateBookProgress = async (progressId, updates) => {
  try {
    await updateDoc(doc(db, 'bookProgress', progressId), {
      ...updates,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  } catch (error) {
    console.error('Update book progress error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get book progress by book ID
 */
export const getBookProgressByBookId = async (bookId) => {
  try {
    const q = query(collection(db, 'bookProgress'), where('bookId', '==', bookId));
    const progressSnapshot = await getDocs(q);
    const progress = progressSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, progress };
  } catch (error) {
    console.error('Get book progress error:', error);
    return { success: false, error: error.message };
  }
};

// ============================================
// ACTIVITIES FUNCTIONS
// ============================================

/**
 * Create activity
 */
export const createActivity = async (activityData) => {
  try {
    const activityRef = doc(collection(db, 'activities'));
    const activity = {
      id: activityRef.id,
      ...activityData,
      timestamp: serverTimestamp()
    };

    await setDoc(activityRef, activity);
    return { success: true, activityId: activityRef.id };
  } catch (error) {
    console.error('Create activity error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get activities for user's books
 */
export const getUserActivities = async (userId) => {
  try {
    const activitiesSnapshot = await getDocs(collection(db, 'activities'));
    const activities = activitiesSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, activities };
  } catch (error) {
    console.error('Get user activities error:', error);
    return { success: false, error: error.message };
  }
};


// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Check if user has claimed a book
 */
export const hasUserClaimedBook = async (userId, bookId) => {
  try {
    const q = query(
      collection(db, 'claimedBooks'),
      where('userId', '==', userId),
      where('bookId', '==', bookId)
    );
    const snapshot = await getDocs(q);
    return { success: true, claimed: !snapshot.empty };
  } catch (error) {
    console.error('Check claimed book error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Check if user has reviewed a book
 */
export const hasUserReviewedBook = async (userId, bookId) => {
  try {
    const q = query(
      collection(db, 'reviews'),
      where('reviewerId', '==', userId),
      where('bookId', '==', bookId)
    );
    const snapshot = await getDocs(q);
    return { success: true, reviewed: !snapshot.empty };
  } catch (error) {
    console.error('Check reviewed book error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get book progress for a specific user
 */
export const getUserBookProgress = async (userId) => {
  try {
    const q = query(collection(db, 'bookProgress'), where('reviewerId', '==', userId));
    const progressSnapshot = await getDocs(q);
    const progress = progressSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return { success: true, progress };
  } catch (error) {
    console.error('Get user book progress error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listen to book progress in real-time for a user
 */
export const listenToUserBookProgress = (userId, callback) => {
  const q = query(collection(db, 'bookProgress'), where('reviewerId', '==', userId));
  return onSnapshot(q, (snapshot) => {
    const progress = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(progress);
  });
};

/**
 * Listen to claimed books in real-time for a user
 */
export const listenToUserClaimedBooks = (userId, callback) => {
  const q = query(collection(db, 'claimedBooks'), where('userId', '==', userId));
  return onSnapshot(q, (snapshot) => {
    const claims = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    callback(claims);
  });
};

/**
 * Listen to user changes in real-time
 */
export const listenToUser = (userId, callback) => {
  return onSnapshot(doc(db, 'users', userId), (docSnapshot) => {
    if (docSnapshot.exists()) {
      const updatedUser = { id: docSnapshot.id, ...docSnapshot.data() };
      callback(updatedUser);
    }
  });
};


/**
 * Check if email is verified
 */
export const checkEmailVerified = async () => {
  try {
    const user = auth.currentUser;
    if (!user) return { success: false, verified: false };

    await reload(user);
    return { success: true, verified: user.emailVerified };
  } catch (error) {
    console.error('Check email verified error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Resend the verification email.
 * Accepts an explicit Firebase Auth user object (needed on the verify-email
 * screen, where the user was already signed out and auth.currentUser is
 * gone) and falls back to auth.currentUser if none is passed.
 */
export const resendVerificationEmail = async (user) => {
  try {
    const target = user || auth.currentUser;
    if (!target) {
      return { success: false, error: 'No user session found. Please log in again to resend.' };
    }
    await sendEmailVerification(target);
    return { success: true };
  } catch (error) {
    console.error('Resend verification email error:', error);
    return { success: false, error: error.message };
  }
};

/**
 * LoadingSpinner component
 */
export const LoadingSpinner = () => {
  return React.createElement('div', {
    className: 'flex items-center justify-center p-4'
  }, React.createElement('div', {
    className: 'animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600'
  }));
};



// ============================================
// ADD THESE FUNCTIONS TO firebaseService.js
// ============================================

/**
 * Mark lock as paid (when admin approves review)
 * The coins were already deducted at claim time - now just mark as paid
 * and add coins to reviewer
 */
export const markLockAsPaid = async (bookId, reviewerId) => {
  try {
    // Find the active lock for this book + reviewer
    const q = query(
      collection(db, 'coinLocks'),
      where('bookId', '==', bookId),
      where('reviewerId', '==', reviewerId),
      where('status', '==', 'locked')
    );
    const snapshot = await getDocs(q);

    if (snapshot.empty) {
      console.warn('No active lock found for this review payment');
      return { success: false, error: 'No lock found' };
    }

    const lockDoc = snapshot.docs[0];
    const lock = lockDoc.data();

    // Mark lock as paid
    await updateDoc(lockDoc.ref, {
      status: 'paid',
      paidAt: serverTimestamp()
    });

    return { success: true, coinsLocked: lock.coinsLocked };
  } catch (error) {
    console.error('Mark lock as paid error:', error);
    return { success: false, error: error.message };
  }
};

